import * as React from "react";
import { Card, LoadingOverlay, Text, useMantineColorScheme } from "@mantine/core";
import { renderChart, type ChartDefinition, type ChartData } from "@/charts";

interface ChartViewProps {
    chart: {
        id: string;
        type: string;
        config: any;
    };
    chartData: ChartData;
    isExecuting: boolean;
    height?: string;
    minHeight?: string;
}

export const ChartView: React.FC<ChartViewProps> = ({
    chart,
    chartData,
    isExecuting,
    height = '75vh',
    minHeight = '600px'
}) => {
    const { colorScheme } = useMantineColorScheme();
    const chartContainerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (
            !chartContainerRef.current ||
            chartData.length === 0 ||
            !chart ||
            isExecuting
        ) {
            return;
        }

        try {
            const definition: ChartDefinition = {
                type: chart.type as any,
                config: chart.config as any,
            };

            const container = chartContainerRef.current;
            const containerWidth = container.clientWidth || 800;
            const chartWidth = containerWidth;
            const chartHeight = Math.max(window.innerHeight * 0.75, 600);

            void renderChart(chartContainerRef.current, chartData, definition, {
                width: chartWidth,
                height: chartHeight,
                margin: { top: 40, right: 32, bottom: 56, left: 64 },
            }, (colorScheme === 'dark' ? 'dark' : 'light'));
        } catch (err) {
            console.error("Failed to render chart:", err);
        }
    }, [chartData, chart, isExecuting, colorScheme]);

    return (
        <Card
            withBorder
            className="rounded-md border"
            style={{
                backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : undefined,
                borderColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : undefined,
                minHeight: minHeight,
                position: 'relative'
            }}
        >
            <LoadingOverlay
                visible={isExecuting}
                overlayProps={{ radius: "sm", opacity: 0 }}
            />
            <Card.Section inheritPadding py="md">
                {chartData.length > 0 ? (
                    <div className="w-full overflow-x-auto">
                        <div
                            ref={chartContainerRef}
                            className="w-full"
                            style={{ height, minHeight }}
                        />
                    </div>
                ) : !isExecuting && (
                    <div className="flex items-center justify-center py-8">
                        <Text c="dimmed">No data available to render chart</Text>
                    </div>
                )}
            </Card.Section>
        </Card>
    );
};
